/**
 * Integrytas Group - Base App (MOBILE)
 * Page: PFW.MEMBERSHIP — mobile-native portal membership list.
 *
 * Touch-first counterpart of src/pages/admin/PfwMembershipPage.tsx. A DISTINCT
 * presentation (dense card list + facet chips + status pills + bottom-sheet
 * detail) over the SAME data/logic layer as the desktop page — never the desktop
 * grid re-hosted in a phone viewport (§16):
 *   - reads via apiDb: pfw_member joined to pfw_company (company name) and
 *     csm_user_master (the member's display name / email), so every FK is resolved
 *     to a human-readable name and no raw id reaches render (§17.B4).
 *   - approve/reject a PENDING member routes through the PFW domain RPC
 *     pfw_approve_member exactly like the desktop page. That RPC IS the sanctioned
 *     write path for this action: it enforces the company-admin (or CSM-admin)
 *     authorization, stamps approved_by_user_id, and commits through the Common
 *     Routine (§2). It is an RPC call, not a direct apiDb insert/update/delete —
 *     writing pfw_member.status by hand via routedUpdate would bypass that
 *     authorization + stamp and is intentionally NOT done (same rationale as
 *     MobileMyApprovalsPage). Hence routed_writes is not imported here.
 *
 * The desktop page also hosts a "register a company" form; company self-
 * registration is an external-user onboarding flow, not part of the mobile admin
 * membership-review surface, so it is deliberately omitted here.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, Check, Ban, X, UserRound } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { useActiveLocale } from '../../lib/use_active_locale';
import { useFormatDateTime } from '../../lib/format_date';
import { MobilePageHeader, MobileStatStrip, MobileContentCard, MobileCardRow, MobileDetailRow, MobileFilterChips, MobileStatusPill, type MobilePillTone } from '../components';
import { MobileRelatedChildren } from '../components/MobileRelatedChildren';
import type { MobilePageComponentProps } from '../mobile_page_components';

// §17.B11: membership status is a CHECK enum (PENDING/ACTIVE/REJECTED) — labels go
// through i18n and never render as the bare stored literal; each maps to a pill tone.
const STATUS_META: Record<string, { key: string; label: string; tone: MobilePillTone }> = {
  PENDING:  { key: 'mobile.pfw_members.status.PENDING',  label: 'Pending',  tone: 'wait' },
  ACTIVE:   { key: 'mobile.pfw_members.status.ACTIVE',   label: 'Active',   tone: 'ok' },
  REJECTED: { key: 'mobile.pfw_members.status.REJECTED', label: 'Rejected', tone: 'no' },
};

// §17.B11: member_role is a CHECK enum (ADMIN/MEMBER) — labelled constant, not a
// bare literal in the JSX.
const ROLE_META: Record<string, { key: string; label: string }> = {
  ADMIN:  { key: 'mobile.pfw_members.role.ADMIN',  label: 'Admin' },
  MEMBER: { key: 'mobile.pfw_members.role.MEMBER', label: 'Member' },
};

type StatusFacet = 'all' | 'PENDING' | 'ACTIVE' | 'REJECTED';

interface MobileMemberRow {
  pfw_member_id: number;
  member_role: string;
  status: string;
  created_at: string;
  company_code: string;
  company_name: string;
  user_name: string;
  user_email: string;
}

interface PfwApproveResult { ok: boolean; error_code?: string; status?: string }

function initialsOf(name: string, email: string): string {
  const base = (name || email || '?').trim();
  const parts = base.split(/[\s@.]+/).filter(Boolean);
  const chars = parts.length >= 2 ? parts[0][0] + parts[1][0] : base.slice(0, 2);
  return chars.toUpperCase();
}

export function MobilePfwMembershipPage(_props: MobilePageComponentProps) {
  const { t } = useTranslation();
  // The active locale does not change these labels (member name/company name are
  // not _tl-translated), but is kept as a reload trigger for parity with the other
  // mobile screens should locale-sensitive lookups be added later.
  const activeLocale = useActiveLocale();
  const formatDateTime = useFormatDateTime();
  const [members, setMembers] = useState<MobileMemberRow[]>([]);
  const [filter, setFilter] = useState('');
  const [statusFacet, setStatusFacet] = useState<StatusFacet>('PENDING');
  const [selected, setSelected] = useState<MobileMemberRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data, error: e } = await apiDb
        .from('pfw_member')
        .select(`
          pfw_member_id, member_role, status, created_at,
          pfw_company ( internal_code, name ),
          member_user:csm_user_master!fk_pfw_member_user ( display_name, email )
        `)
        .order('created_at', { ascending: false });
      if (e) throw e;

      type RawMember = {
        pfw_member_id: number;
        member_role: string;
        status: string;
        created_at: string;
        pfw_company: { internal_code: string; name: string } | null;
        member_user: { display_name: string; email: string } | null;
      };
      const rows: MobileMemberRow[] = ((data ?? []) as RawMember[]).map((m) => ({
        pfw_member_id: m.pfw_member_id,
        member_role: m.member_role,
        status: m.status,
        created_at: m.created_at,
        company_code: m.pfw_company?.internal_code ?? '',
        company_name: m.pfw_company?.name ?? m.pfw_company?.internal_code ?? '',
        user_name: m.member_user?.display_name ?? m.member_user?.email ?? '',
        user_email: m.member_user?.email ?? '',
      }));
      setMembers(rows);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Re-run on mount and whenever the active locale changes (parity with sibling
  // mobile screens). t is intentionally NOT a dependency (fresh identity each
  // render in tests would loop) — mirrors MobileRoleListPage / MobileUserListPage.
  useEffect(() => { void reload(); }, [activeLocale]);

  const statusLabel = (status: string) => {
    const meta = STATUS_META[status];
    return meta ? t(meta.key, meta.label) : status;
  };
  const statusTone = (status: string): MobilePillTone => STATUS_META[status]?.tone ?? 'info';
  const roleLabel = (role: string) => {
    const meta = ROLE_META[role];
    return meta ? t(meta.key, meta.label) : role;
  };

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return members.filter((m) => {
      if (statusFacet !== 'all' && m.status !== statusFacet) return false;
      if (!q) return true;
      return (
        m.user_name.toLowerCase().includes(q) ||
        m.user_email.toLowerCase().includes(q) ||
        m.company_name.toLowerCase().includes(q) ||
        m.company_code.toLowerCase().includes(q)
      );
    });
  }, [members, filter, statusFacet]);

  const facets: { key: StatusFacet; label: string }[] = [
    { key: 'all', label: t('mobile.pfw_members.all', 'All') },
    { key: 'PENDING', label: t(STATUS_META.PENDING.key, STATUS_META.PENDING.label) },
    { key: 'ACTIVE', label: t(STATUS_META.ACTIVE.key, STATUS_META.ACTIVE.label) },
    { key: 'REJECTED', label: t(STATUS_META.REJECTED.key, STATUS_META.REJECTED.label) },
  ];

  // Approve/reject via the PFW domain RPC (mirrors the desktop page). RPC, not a
  // direct table write — see the header note for why routedUpdate is not used.
  const decide = async (m: MobileMemberRow, decision: 'APPROVE' | 'REJECT') => {
    setActionBusy(true);
    setError(null);
    try {
      const { data, error: e } = await apiDb.rpc<PfwApproveResult | null>('pfw_approve_member', {
        p_member_id: m.pfw_member_id,
        p_decision: decision,
      });
      if (e) throw e;
      if (data && data.ok === false) throw new Error(data.error_code ?? 'PFW.APPROVE_FAILED');
      setSelected(null);
      await reload();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setActionBusy(false);
    }
  };

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.pfw_members.title', 'Portal Members')}
        description={t('mobile.pfw_members.description', 'Company portal membership requests and their approval status.')}
      />

      <MobileStatStrip
        stats={[
          { value: members.length, label: t('mobile.pfw_members.stat.total', 'Total') },
          { value: members.filter((m) => m.status === 'PENDING').length, label: t(STATUS_META.PENDING.key, STATUS_META.PENDING.label) },
          { value: members.filter((m) => m.status === 'ACTIVE').length, label: t(STATUS_META.ACTIVE.key, STATUS_META.ACTIVE.label) },
        ]}
      />

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.pfw_members.search', 'Search members')}
          aria-label={t('mobile.pfw_members.search', 'Search members')}
        />
      </div>

      <MobileFilterChips
        chips={facets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => statusFacet === v}
        onSelect={(v) => setStatusFacet(v as StatusFacet)}
      />

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.pfw_members.none', 'No members match')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((m) => (
            <MobileCardRow
              key={m.pfw_member_id}
              lead={initialsOf(m.user_name, m.user_email)}
              leadTone="blue"
              title={m.user_name || m.user_email}
              subtitle={`${m.company_name} · ${roleLabel(m.member_role)}`}
              trailing={<MobileStatusPill tone={statusTone(m.status)}>{statusLabel(m.status)}</MobileStatusPill>}
              chevron
              onClick={() => setSelected(m)}
            />
          ))}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={() => { if (!actionBusy) setSelected(null); }} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.pfw_members.detail', 'Member detail')}>
        <div className="m-grabber" />
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av">{initialsOf(selected.user_name, selected.user_email)}</span>
              <span>
                <div className="m-nm">{selected.user_name || selected.user_email}</div>
                <div className="m-em">{selected.user_email}</div>
              </span>
            </div>
            <MobileContentCard>
              <MobileDetailRow label={t('mobile.pfw_members.company', 'Company')} value={selected.company_name} />
              <MobileDetailRow label={t('mobile.pfw_members.role_lbl', 'Role')} value={roleLabel(selected.member_role)} />
              <MobileDetailRow
                label={t('mobile.pfw_members.status_lbl', 'Status')}
                value={<MobileStatusPill tone={statusTone(selected.status)}>{statusLabel(selected.status)}</MobileStatusPill>}
              />
              <MobileDetailRow label={t('mobile.pfw_members.requested', 'Requested')} value={formatDateTime(selected.created_at)} />
            </MobileContentCard>
            {/* §17.B13 — the row's related records, stacked one group per block. */}
            <MobileContentCard>
              <MobileRelatedChildren groups={[
                  { key: 'company', title: t('mobile.pfw_members.related.company', 'Company'),
                  items: selected.company_name ? [{ id: selected.company_name, label: selected.company_name }] : [] },
                ]} />
            </MobileContentCard>
            <div className="m-actionbar">
              {selected.status === 'PENDING' && (
                <>
                  <button
                    type="button"
                    className="m-btn m-btn-primary"
                    disabled={actionBusy}
                    onClick={() => void decide(selected, 'APPROVE')}
                  >
                    <Check size={16} />
                    {t('mobile.pfw_members.approve', 'Approve')}
                  </button>
                  <button
                    type="button"
                    className="m-btn m-btn-danger"
                    disabled={actionBusy}
                    onClick={() => void decide(selected, 'REJECT')}
                  >
                    <Ban size={16} />
                    {t('mobile.pfw_members.reject', 'Reject')}
                  </button>
                </>
              )}
              {selected.status !== 'PENDING' && (
                <span className="m-formrow-ro">
                  <UserRound size={16} /> {t('mobile.pfw_members.decided', 'Already {{status}}', { status: statusLabel(selected.status) })}
                </span>
              )}
              <button type="button" className="m-btn m-btn-ghost" disabled={actionBusy} onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
